import { journalTemplatePresets, JournalTemplateId } from './journalTemplates';

export function mmToPx(mm: number): number {
  // 96 dpi and 25.4 mm per inch
  return Math.round((mm / 25.4) * 96);
}

export function pxToMm(px: number): number {
  return Math.round((px / 96) * 25.4);
}

export function applyJournalTemplate(plotlyJson: any, id: JournalTemplateId, options?: { skipSizeChanges?: boolean }): { previous: any } {
  const prev = JSON.parse(JSON.stringify({
    width: plotlyJson?.layout?.width,
    height: plotlyJson?.layout?.height,
    font: plotlyJson?.layout?.font,
    margin: plotlyJson?.layout?.margin,
    legend: plotlyJson?.layout?.legend,
    export: (plotlyJson as any)?.export,
  }));

  const t = journalTemplatePresets.find(x => x.id === id);
  if (!t) return { previous: prev };

  if (!plotlyJson.layout) plotlyJson.layout = {};

  // Typography: Journal templates specify print sizes (pt), but for on-screen viewing we need larger fonts
  // Scale factor: 1.5x-2x for screen readability while maintaining relative proportions
  // This ensures templates look good on screen while still respecting journal typography guidelines
  const SCREEN_SCALE_FACTOR = 1.8; // Scale print sizes up for screen readability
  
  const fam = (t.typography as any).fontFamily?.[0] || plotlyJson.layout?.font?.family || 'Arial';
  let sizePt = (t.typography as any).fontSizePt_min || (t.typography as any).fontSizePt_range?.[0] || 10;
  // For Cell, prefer the top of the journal's 6–8 pt range for readability in-editor
  if (id === 'cell' && (t.typography as any).fontSizePt_range) {
    const range:any = (t.typography as any).fontSizePt_range;
    sizePt = range[1] || range[0] || sizePt;
  }
  // Scale print sizes up for screen: convert pt to px and apply scale factor
  // Minimum 10px for readability, but scale proportionally from template sizes
  const sizePx = Math.max(10, Math.round(sizePt * SCREEN_SCALE_FACTOR));
  plotlyJson.layout.font = { family: fam, size: sizePx, color: plotlyJson.layout?.font?.color || '#000' };
  
  // Title font size: scale proportionally (typically 1.2-1.5x the base font size for prominence)
  const titleSizePx = Math.max(14, Math.round(sizePt * 1.3 * SCREEN_SCALE_FACTOR));
  const subtitleSizePx = Math.max(10, Math.round(sizePt * 0.9 * SCREEN_SCALE_FACTOR));
  
  // Initialize title if it doesn't exist (so font size is set for when user adds title)
  if (!plotlyJson.layout.title) {
    plotlyJson.layout.title = {};
  }
  
  // Update title font size and family
  // Always apply template font, even if title already has font settings
  if (!plotlyJson.layout.title.font) plotlyJson.layout.title.font = {};
  const oldTitleFont = { ...plotlyJson.layout.title.font };
  // Force update font family and size to match template
  plotlyJson.layout.title.font.size = titleSizePx;
  plotlyJson.layout.title.font.family = fam;
  // Preserve color if it exists, otherwise use default
  plotlyJson.layout.title.font.color = plotlyJson.layout.title.font.color || plotlyJson.layout?.font?.color || '#000';
  if (oldTitleFont.family !== fam || oldTitleFont.size !== titleSizePx) {
    console.log(`📝 [applyJournalTemplate] Title font updated: ${oldTitleFont.family || 'default'} → ${fam}, ${oldTitleFont.size || 'default'}pt → ${titleSizePx}px`);
  }
  
  // If subtitle exists, also update its font size (typically same as base or slightly smaller)
  if (plotlyJson.layout.title.subtitle) {
    // Subtitle can be a string or an object with text and font properties
    if (typeof plotlyJson.layout.title.subtitle === 'object' && plotlyJson.layout.title.subtitle !== null) {
      if (!plotlyJson.layout.title.subtitle.font) plotlyJson.layout.title.subtitle.font = {};
      plotlyJson.layout.title.subtitle.font.size = subtitleSizePx;
      plotlyJson.layout.title.subtitle.font.family = fam;
      if (plotlyJson.layout.title.subtitle.font.color === undefined) {
        plotlyJson.layout.title.subtitle.font.color = plotlyJson.layout?.font?.color || '#000';
      }
    }
    // If subtitle is just a string, we'll need to convert it to an object when font properties are needed
    // For now, store the font size in a way that can be applied later
  }
  
  // Update x-axis title font (axis titles typically use base font size)
  // Always apply template font, even if axis already has font settings
  if (!plotlyJson.layout.xaxis) plotlyJson.layout.xaxis = {};
  if (!plotlyJson.layout.xaxis.title) plotlyJson.layout.xaxis.title = {};
  if (!plotlyJson.layout.xaxis.title.font) plotlyJson.layout.xaxis.title.font = {};
  const oldXAxisFont = { ...plotlyJson.layout.xaxis.title.font };
  // Force update font family and size to match template
  plotlyJson.layout.xaxis.title.font.size = sizePx;
  plotlyJson.layout.xaxis.title.font.family = fam;
  plotlyJson.layout.xaxis.title.font.color = plotlyJson.layout.xaxis.title.font.color || plotlyJson.layout?.font?.color || '#000';
  if (oldXAxisFont.family !== fam || oldXAxisFont.size !== sizePx) {
    console.log(`📝 [applyJournalTemplate] X-axis title font updated: ${oldXAxisFont.family || 'default'} → ${fam}, ${oldXAxisFont.size || 'default'}pt → ${sizePx}px`);
  }
  
  // Update y-axis title font (axis titles typically use base font size)
  // Always apply template font, even if axis already has font settings
  if (!plotlyJson.layout.yaxis) plotlyJson.layout.yaxis = {};
  if (!plotlyJson.layout.yaxis.title) plotlyJson.layout.yaxis.title = {};
  if (!plotlyJson.layout.yaxis.title.font) plotlyJson.layout.yaxis.title.font = {};
  const oldYAxisFont = { ...plotlyJson.layout.yaxis.title.font };
  // Force update font family and size to match template
  plotlyJson.layout.yaxis.title.font.size = sizePx;
  plotlyJson.layout.yaxis.title.font.family = fam;
  plotlyJson.layout.yaxis.title.font.color = plotlyJson.layout.yaxis.title.font.color || plotlyJson.layout?.font?.color || '#000';
  if (oldYAxisFont.family !== fam || oldYAxisFont.size !== sizePx) {
    console.log(`📝 [applyJournalTemplate] Y-axis title font updated: ${oldYAxisFont.family || 'default'} → ${fam}, ${oldYAxisFont.size || 'default'}pt → ${sizePx}px`);
  }
  
  // Also update axis tick fonts to match template
  if (!plotlyJson.layout.xaxis.tickfont) plotlyJson.layout.xaxis.tickfont = {};
  plotlyJson.layout.xaxis.tickfont.size = sizePx;
  plotlyJson.layout.xaxis.tickfont.family = fam;
  plotlyJson.layout.xaxis.tickfont.color = plotlyJson.layout.xaxis.tickfont.color || plotlyJson.layout?.font?.color || '#000';
  
  if (!plotlyJson.layout.yaxis.tickfont) plotlyJson.layout.yaxis.tickfont = {};
  plotlyJson.layout.yaxis.tickfont.size = sizePx;
  plotlyJson.layout.yaxis.tickfont.family = fam;
  plotlyJson.layout.yaxis.tickfont.color = plotlyJson.layout.yaxis.tickfont.color || plotlyJson.layout?.font?.color || '#000';
  
  // Store template font sizes for future title/subtitle additions
  // This ensures that if user adds title/subtitle after template is applied, they get correct sizes
  if (!(plotlyJson.layout as any)._templateFontSizes) {
    (plotlyJson.layout as any)._templateFontSizes = {};
  }
  (plotlyJson.layout as any)._templateFontSizes.title = titleSizePx;
  (plotlyJson.layout as any)._templateFontSizes.subtitle = subtitleSizePx;
  (plotlyJson.layout as any)._templateFontSizes.base = sizePx;

  // Size policy handling by template type
  // Skip size changes if requested (e.g., in standalone plot editor to maintain consistent sizing)
  // Size changes should only happen in figure composition context where normalization is applied
  const skipSizeChanges = options?.skipSizeChanges ?? false;
  const sp:any = t.sizePolicy;
  if (!skipSizeChanges && sp?.widths_mm?.length) {
    // Ensure width is set to one of the allowed column widths (nearest)
    const currentMm = typeof plotlyJson.layout.width === 'number' ? pxToMm(plotlyJson.layout.width) : undefined;
    const target = (function(){
      if (typeof currentMm === 'number') {
        return sp.widths_mm.reduce((best:number, w:number) => Math.abs(w - currentMm) < Math.abs(best - currentMm) ? w : best, sp.widths_mm[0]);
      }
      // Default to the closest to ~90mm (typical single column) if no width specified
      const preferred = 90;
      return sp.widths_mm.reduce((best:number, w:number) => Math.abs(w - preferred) < Math.abs(best - preferred) ? w : best, sp.widths_mm[0]);
    })();
    const targetWidthPx = mmToPx(target);
    // Maintain aspect ratio if user had a concrete size
    const prevW = typeof plotlyJson.layout.width === 'number' ? plotlyJson.layout.width : undefined;
    const prevH = typeof plotlyJson.layout.height === 'number' ? plotlyJson.layout.height : undefined;
    plotlyJson.layout.width = targetWidthPx;
    if (typeof prevW === 'number' && typeof prevH === 'number' && (!sp.presets_cm && !sp.maxSize_in)) {
      let newH = Math.round((prevH / prevW) * targetWidthPx);
      // Respect maxHeight if present
      if (sp?.maxHeight_mm) {
        const maxPx = mmToPx(sp.maxHeight_mm);
        if (newH > maxPx) newH = maxPx;
      }
      plotlyJson.layout.height = newH;
    }
    plotlyJson.layout.autosize = false;
  }
  // Do not force a height when only a max is provided; clamp only if existing height exceeds max
  if (!skipSizeChanges && sp?.maxHeight_mm && typeof plotlyJson.layout.height === 'number') {
    const maxPx = mmToPx(sp.maxHeight_mm);
    if (plotlyJson.layout.height > maxPx) {
      plotlyJson.layout.height = maxPx;
    }
  }
  if (!skipSizeChanges && sp?.presets_cm) {
    // Pick medium if available else first, and apply explicitly for compliance
    const cm = (sp.presets_cm.medium || Object.values(sp.presets_cm)[0]) as [number, number];
    const widthMm = cm[0] * 10;
    const heightMm = cm[1] * 10;
    plotlyJson.layout.width = mmToPx(widthMm);
    plotlyJson.layout.height = mmToPx(heightMm);
    plotlyJson.layout.autosize = false;
  }
  if (!skipSizeChanges && sp?.maxSize_in) {
    const widthIn = sp.maxSize_in[0];
    const heightIn = sp.maxSize_in[1];
    const maxW = Math.round(widthIn * 96);
    const maxH = Math.round(heightIn * 96);
    // Treat as maxima, not exact sizes
    if (typeof plotlyJson.layout.width === 'number' && plotlyJson.layout.width > maxW) {
      plotlyJson.layout.width = maxW;
    }
    if (typeof plotlyJson.layout.height === 'number' && plotlyJson.layout.height > maxH) {
      plotlyJson.layout.height = maxH;
    }
  }
  if (!skipSizeChanges && sp?.width_mm_range) {
    const range:any = sp.width_mm_range as [number, number];
    const mid = (range[0] + range[1]) / 2;
    const minPx = mmToPx(range[0]);
    const maxPx = mmToPx(range[1]);
    const current = typeof plotlyJson.layout.width === 'number' ? plotlyJson.layout.width : mmToPx(mid);
    plotlyJson.layout.width = Math.min(Math.max(current, minPx), maxPx);
    plotlyJson.layout.autosize = false;
  }

  // If only one side specified, auto-calc the other to 3:2 ratio
  // Do not force an aspect ratio or minimum pixels; if a template
  // doesn't specify one side, we leave it undefined so autosize preserves
  // the author's aspect.

  // Legend styling (do not override user placement unless template dictates it)
  if (!plotlyJson.layout.legend) plotlyJson.layout.legend = {};
  // Update legend font to match template (legend typically uses base font size)
  if (!(plotlyJson.layout.legend as any).font) (plotlyJson.layout.legend as any).font = {};
  const oldLegendFont = { ...((plotlyJson.layout.legend as any).font || {}) };
  (plotlyJson.layout.legend as any).font.size = sizePx;
  (plotlyJson.layout.legend as any).font.family = fam;
  (plotlyJson.layout.legend as any).font.color = (plotlyJson.layout.legend as any).font.color || '#000000';
  (plotlyJson.layout.legend as any).borderwidth = (plotlyJson.layout.legend as any).borderwidth ?? 0;
  if (oldLegendFont.family !== fam || oldLegendFont.size !== sizePx) {
    console.log(`📝 [applyJournalTemplate] Legend font updated: ${oldLegendFont.family || 'default'} → ${fam}, ${oldLegendFont.size || 'default'}pt → ${sizePx}px`);
  }
  
  // Log summary of all font updates
  console.log(`🎨 [applyJournalTemplate] Applied journal template "${id}":`, {
    fontFamily: fam,
    baseFontSize: `${sizePx}px (${sizePt}pt)`,
    titleFontSize: `${titleSizePx}px`,
    subtitleFontSize: `${subtitleSizePx}px`,
    updatedElements: {
      title: !!plotlyJson.layout.title,
      subtitle: !!plotlyJson.layout.title?.subtitle,
      xAxisTitle: !!plotlyJson.layout.xaxis?.title,
      yAxisTitle: !!plotlyJson.layout.yaxis?.title,
      legend: !!plotlyJson.layout.legend,
    },
  });

  // DPI/export policy shim (store on layout._export)
  (plotlyJson.layout as any)._export = { dpi: (t.dpiPolicy as any).final || (t.dpiPolicy as any).combination || 300 };

  // Apply minimum line weights per template where specified
  const minLinePt: number | undefined = (t.typography as any).lineWeightPt_min
    ?? ((t.typography as any).lineWeightPt_range ? (t.typography as any).lineWeightPt_range[0] : undefined);

  if (Array.isArray(plotlyJson.data) && typeof minLinePt === 'number') {
    for (const tr of plotlyJson.data) {
      // Trace line width
      if (!tr.line) tr.line = {};
      if (!tr.line.width || tr.line.width < minLinePt) tr.line.width = minLinePt;
      // Marker outline widths (bars/markers)
      if (tr.marker) {
        if (!tr.marker.line) tr.marker.line = {};
        if (!tr.marker.line.width || tr.marker.line.width < minLinePt) tr.marker.line.width = minLinePt;
      }
    }
  }

  // Journal-specific DPI tweaks
  if (id === 'acs_generic') {
    (plotlyJson.layout as any)._export.dpi = 600;
  }
  if (id === 'science') {
    (plotlyJson.layout as any)._export.dpi = 300;
    if (plotlyJson.layout?.font?.size && plotlyJson.layout.font.size < 9) plotlyJson.layout.font.size = 9;
  }
  if (id === 'nature') {
    (plotlyJson.layout as any)._export.dpi = 600;
  }
  // no extra overrides for science_advances here; any per-plot overrides should be applied by the editor

  // Margins: keep user/editor margins as-is; do not auto-reserve space
  // (journals do not mandate a fixed title-to-plot gap).

  return { previous: prev };
}


